/**
 * Iter T · Phase 1 — Admin Quotes tab container.
 * List ↔ Detail switcher. Deep-linkable via ?open=<public_id> so
 * `/admin/quotes/:public_id` (redirected in App.js) opens the detail
 * pane directly and preserves history — restoring the "Original Quote"
 * link from Fulfillment Admin (previously 404'd).
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import QuoteList from "@/components/admin/QuoteList";
import QuoteDetail from "@/components/admin/QuoteDetail";
import QuoteAnalytics from "@/components/admin/QuoteAnalytics";
import RecoveryHealthCard from "@/components/admin/RecoveryHealthCard";


export default function QuotesAdmin() {
  const location = useLocation();
  const nav = useNavigate();
  const [openId, setOpenId] = useState(null);

  // Hydrate openId from `?open=` and keep it in sync when the query
  // param changes (e.g. redirect from /admin/quotes/:public_id).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const open = params.get("open");
    if (open && open !== openId) setOpenId(open);
    if (!open && openId) setOpenId(null);
    // We intentionally only depend on location.search — openId changes
    // driven by the user (List → Detail click) call handleOpen / back()
    // to update the URL, so this effect will not overwrite them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const writeOpenParam = (val) => {
    const params = new URLSearchParams(location.search);
    params.set("tab", "quotes");
    if (val) params.set("open", val); else params.delete("open");
    nav({ pathname: "/admin", search: "?" + params.toString(), hash: location.hash }, { replace: false });
  };

  const handleOpen = (id) => { setOpenId(id); writeOpenParam(id); };
  const handleBack = () => { setOpenId(null); writeOpenParam(null); };

  return (
    <div data-testid="quotes-admin">
      {openId ? (
        // FIRST-OPEN HYDRATION FIX (Msg 241) — `key={openId}` forces a
        // clean remount when the admin switches between quotes. This
        // guarantees QuoteAdjustmentsPanel (whose useState is only
        // seeded ONCE from props) always re-initializes with the
        // freshly-fetched quote data. Prevents Quote A → Quote B
        // state carryover for discounts, custom items, editable
        // pricing controls, and unit-price overrides.
        <QuoteDetail key={openId} publicId={openId} onBack={handleBack} />
      ) : (
        <>
          <RecoveryHealthCard />
          <QuoteAnalytics />
          <QuoteList onOpen={handleOpen} />
        </>
      )}
    </div>
  );
}
